import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import Button from '../ui/Button';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const navLinks = [
    { name: 'Start', path: '/' },
    { name: 'Über mich', path: '/ueber-mich' },
    { name: 'Leistungen', path: '/leistungen' },
    { name: 'Pakete', path: '/pakete' },
  ];

  const isHomePage = location.pathname === '/';

  return (
    <header className="fixed top-0 w-full z-50 flex justify-center px-4 pt-4 md:pt-6 transition-all duration-500 pointer-events-none">
      <div className={`pointer-events-auto transition-all duration-500 ease-out flex items-center justify-between px-6 py-3 rounded-full 
        ${scrolled 
          ? 'w-full max-w-5xl glass-dark shadow-2xl' 
          : `w-full max-w-7xl bg-transparent py-4 ${isHomePage ? 'text-white' : 'text-brand-dark'}`
        }`}
      >
        <Link to="/" className={`text-xl md:text-2xl font-serif font-bold tracking-widest uppercase z-50 flex-shrink-0 transition-colors ${scrolled || isHomePage ? 'text-white' : 'text-brand-dark'}`}>
          Glow<span className="text-brand-accent">Consulting</span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-10 absolute left-1/2 -translate-x-1/2">
          {navLinks.map((link) => {
            const isActive = location.pathname === link.path;
            const textColor = scrolled || isHomePage 
              ? (isActive ? 'text-brand-accent' : 'text-white/80 hover:text-white')
              : (isActive ? 'text-brand-accent' : 'text-brand-text hover:text-brand-dark');

            return (
              <Link 
                key={link.path} 
                to={link.path}
                className={`text-xs uppercase tracking-[0.15em] transition-all duration-300 font-medium relative group ${textColor}`}
              >
                {link.name}
                <span className={`absolute -bottom-2 left-1/2 w-1 h-1 bg-brand-accent rounded-full transition-all duration-300 -translate-x-1/2 ${isActive ? 'opacity-100 scale-100' : 'opacity-0 scale-0 group-hover:opacity-100 group-hover:scale-100'}`}></span>
              </Link>
            )
          })}
        </nav>

        <div className="hidden lg:block flex-shrink-0">
          <Button 
            to="/kontakt" 
            variant={scrolled || isHomePage ? "primary" : "outline"} 
            className={`!py-2.5 !px-6 text-xs ${scrolled || isHomePage ? '!bg-brand-accent !text-white hover:!bg-brand-dark' : ''}`} 
            showIcon={false}
          >
            Erstgespräch
          </Button>
        </div>

        {/* Mobile Toggle */}
        <button 
          className={`lg:hidden z-50 p-2 rounded-full transition-colors ${scrolled || isHomePage ? 'text-white' : 'text-brand-dark'}`}
          onClick={() => setIsOpen(!isOpen)}
        >
          <SafeIcon name={isOpen ? "X" : "Menu"} className="w-6 h-6" />
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      <div className={`fixed inset-0 bg-brand-dark/95 backdrop-blur-xl z-40 transition-all duration-500 ease-in-out lg:hidden pointer-events-auto
        ${isOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
        <div className="flex flex-col items-center justify-center h-full gap-8 p-6">
          {navLinks.map((link, idx) => (
            <Link 
              key={link.path} 
              to={link.path}
              className="text-4xl font-serif text-white hover:text-brand-accent transition-colors"
              style={{ transitionDelay: `${isOpen ? idx * 50 : 0}ms`, transform: isOpen ? 'translateY(0)' : 'translateY(20px)', opacity: isOpen ? 1 : 0 }}
            >
              {link.name}
            </Link>
          ))}
          <div style={{ transitionDelay: `${isOpen ? navLinks.length * 50 : 0}ms`, transform: isOpen ? 'translateY(0)' : 'translateY(20px)', opacity: isOpen ? 1 : 0 }}>
             <Button to="/kontakt" variant="primary" className="mt-8 !bg-brand-accent !text-white">
              Erstgespräch buchen
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;